import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../api';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState({ favorites: [], seen: [], training_total: 0, training_good: 0, qcm_percent: 0, total_modules: 0 });
  // `dark` reste exposé dans le contexte (rétrocompat pour les composants qui le lisent)
  // mais le toggle a été retiré : le mode est désormais figé sur "Roadbook nocturne".
  const [dark] = useState(true);
  const setDark = () => {}; // no-op
  const [oral, setOral] = useState(() => localStorage.getItem('mc_oral') === '1');
  const [freeMode, setFreeMode] = useState(false);
  const [launchBannerMessage, setLaunchBannerMessage] = useState('');
  const [bannerHidden, setBannerHidden] = useState(() => localStorage.getItem('mc_banner_hidden') === '1');

  // Toujours appliquer la classe dark sur <body>
  useEffect(() => {
    document.body.classList.add('dark');
    document.body.classList.remove('light');
  }, []);

  useEffect(() => {
    document.body.classList.toggle('oral-mode', oral);
    localStorage.setItem('mc_oral', oral ? '1' : '0');
  }, [oral]);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const me = await api.getMe();
      setUser(me);
    } catch (e) {
      localStorage.removeItem('token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshProgress = useCallback(async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const p = await api.getProgress();
      setProgress(p);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  // Fetch public config (FREE_MODE, banner message) once at startup
  useEffect(() => {
    let mounted = true;
    api.getPublicConfig().then(cfg => {
      if (!mounted) return;
      setFreeMode(!!cfg.free_mode);
      setLaunchBannerMessage(cfg.launch_banner_message || '');
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  const dismissBanner = useCallback(() => {
    setBannerHidden(true);
    localStorage.setItem('mc_banner_hidden', '1');
  }, []);

  useEffect(() => {
    if (user) refreshProgress();
  }, [user, refreshProgress]);

  const login = async (email, password) => {
    const data = await api.login(email, password);
    localStorage.setItem('token', data.access_token);
    await refreshUser();
    await refreshProgress();
    return data;
  };

  const loginWithTeacherCode = async (code, pseudo) => {
    const data = await api.teacherCodeLogin(code, pseudo);
    localStorage.setItem('token', data.access_token);
    await refreshUser();
    await refreshProgress();
    return data;
  };

  const register = async (email, password, name, teacherCode) => {
    const data = await api.register(email, password, name, teacherCode);
    localStorage.setItem('token', data.access_token);
    await refreshUser();
    await refreshProgress();
    return data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setProgress({ favorites: [], seen: [], training_total: 0, training_good: 0, qcm_percent: 0, total_modules: 0 });
  };

  const value = {
    user, loading, progress,
    token: (typeof window !== 'undefined' ? localStorage.getItem('token') : null),
    dark, setDark, oral, setOral,
    freeMode, launchBannerMessage, bannerHidden, dismissBanner,
    login, loginWithTeacherCode, register, logout,
    refreshProgress, refreshUser
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
